import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiTrash2 } from "react-icons/fi";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";
import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import { useModal } from "../../../contexts/ModalContext";
import useApi from "../../../hooks/useApi";
import ModalDeleteProssmap from "./ModalDeleteProssmap";

const PERMISSAO_BASE = { modulo: "ssma", submodulo: "prossmap" };

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const Statistics = styled.div`
    width: 280px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const STATUS_RESUMO = ["RASCUNHO", "EM PREENCHIMENTO", "CONCLUÍDO"];

function uniqueSortedStrings(values) {
    return [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
}

function toValueSet(selected) {
    if (!Array.isArray(selected) || selected.length === 0) return null;
    return new Set(
        selected.map((item) =>
            item != null && typeof item === "object" && "value" in item ? item.value : item
        )
    );
}

function aplicarFiltros(lista, filtros) {
    const nomeBusca = filtros.nomeEncarregado.trim().toUpperCase();
    const matBusca = filtros.matricula.trim();

    const equipesSet = toValueSet(filtros.selectedEquipes);
    const centrosSet = toValueSet(filtros.selectedCentrosCusto);
    const statusSet = toValueSet(filtros.selectedStatuses);
    const mesFiltro = filtros.mesReferencia.trim();

    return lista.filter((row) => {
        const nome = String(row.encarregado_nome ?? "").toUpperCase();
        const mat = String(row.encarregado_matricula ?? "").trim();

        if (nomeBusca && !nome.includes(nomeBusca)) return false;
        if (matBusca && !mat.includes(matBusca)) return false;

        if (equipesSet && !equipesSet.has(String(row.equipe ?? ""))) return false;
        if (centrosSet && !centrosSet.has(String(row.centro_custo ?? ""))) return false;
        if (statusSet && !statusSet.has(String(row.status ?? ""))) return false;

        if (mesFiltro) {
            const mesRow = String(row.referencia_mes_label ?? "").trim();
            if (mesRow !== mesFiltro) return false;
        }

        return true;
    });
}

function calcularResumoStatus(lista) {
    const resumo = {
        RASCUNHO: 0,
        "EM PREENCHIMENTO": 0,
        "CONCLUÍDO": 0,
        total: lista.length,
    };

    lista.forEach((row) => {
        const status = String(row.status ?? "").trim();
        if (Object.prototype.hasOwnProperty.call(resumo, status)) {
            resumo[status] += 1;
        }
    });

    return resumo;
}

function formatPontos(valor) {
    const n = Number(valor ?? 0);
    if (Number.isNaN(n)) return "0,00";
    return n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

const Content = ({ refreshKey = 0 }) => {
    const api = useApi();
    const { user } = useContext(AuthContext);
    const { openModal, closeModal, isModalOpen } = useModal();
    const [loading, setLoading] = useState(true);
    const [prossmaps, setProssmaps] = useState([]);
    const [prossmapParaDelete, setProssmapParaDelete] = useState(null);

    const [selectedEquipes, setSelectedEquipes] = useState([]);
    const [selectedCentrosCusto, setSelectedCentrosCusto] = useState([]);
    const [nomeEncarregado, setNomeEncarregado] = useState("");
    const [matricula, setMatricula] = useState("");
    const [mesReferencia, setMesReferencia] = useState("");
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "apagar" }),
        [user]
    );

    const fetchProssmaps = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/Painel/API/Prossmap/List/index.php");
            const lista = data?.data ?? data;
            setProssmaps(Array.isArray(lista) ? lista : []);
        } catch (error) {
            console.error("Erro ao listar PROSSMAPs:", error);
            setProssmaps([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchProssmaps();
    }, [fetchProssmaps, refreshKey]);

    const filtros = useMemo(
        () => ({
            selectedEquipes,
            selectedCentrosCusto,
            nomeEncarregado,
            matricula,
            mesReferencia,
            selectedStatuses,
        }),
        [
            selectedEquipes,
            selectedCentrosCusto,
            nomeEncarregado,
            matricula,
            mesReferencia,
            selectedStatuses,
        ]
    );

    const prossmapsFiltrados = useMemo(
        () => aplicarFiltros(prossmaps, filtros),
        [prossmaps, filtros]
    );

    const resumoStatus = useMemo(
        () => calcularResumoStatus(prossmapsFiltrados),
        [prossmapsFiltrados]
    );

    const opcoesEquipe = useMemo(
        () => uniqueSortedStrings(prossmaps.map((r) => r.equipe)),
        [prossmaps]
    );
    const opcoesCentroCusto = useMemo(
        () => uniqueSortedStrings(prossmaps.map((r) => r.centro_custo)),
        [prossmaps]
    );
    const opcoesEquipeMs = useMemo(
        () => opcoesEquipe.map((opt) => ({ value: opt, label: opt })),
        [opcoesEquipe]
    );
    const opcoesCentroCustoMs = useMemo(
        () => opcoesCentroCusto.map((opt) => ({ value: opt, label: opt })),
        [opcoesCentroCusto]
    );
    const opcoesStatusMs = useMemo(
        () => [
            { value: "RASCUNHO", label: "RASCUNHO" },
            { value: "EM PREENCHIMENTO", label: "EM PREENCHIMENTO" },
            { value: "CONCLUÍDO", label: "CONCLUÍDO" },
        ],
        []
    );

    const abrirDelete = useCallback(
        (row) => {
            if (!podeApagar || !row?.id) return;
            setProssmapParaDelete(row);
            openModal("prossmapDelete");
        },
        [podeApagar, openModal]
    );

    const fecharDelete = useCallback(() => {
        setProssmapParaDelete(null);
        closeModal();
    }, [closeModal]);

    const columns = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessor: "id",
                sortable: true,
                width: "70px",
                align: "center",
            },
            {
                id: "equipe",
                header: "EQUIPE",
                accessor: "equipe",
                sortable: true,
            },
            {
                id: "centro_custo",
                header: "CENTRO DE CUSTO",
                accessor: (row) => row.centro_custo || "-",
                sortable: true,
            },
            {
                id: "contrato",
                header: "CONTRATO",
                accessor: (row) => row.contrato || "-",
                sortable: true,
                width: "120px",
            },
            {
                id: "encarregado",
                header: "ENCARREGADO",
                accessor: "encarregado_nome",
                sortable: true,
            },
            {
                id: "matricula",
                header: "MATRÍCULA",
                accessor: "encarregado_matricula",
                sortable: true,
                width: "110px",
            },
            {
                id: "referencia",
                header: "MÊS REFERÊNCIA",
                accessor: "referencia_mes_label",
                sortable: true,
                width: "140px",
                align: "center",
            },
            {
                id: "pontos",
                header: "PONTOS",
                accessor: (row) => formatPontos(row.pontuacao),
                sortable: true,
                width: "100px",
                align: "center",
                sortValue: (row) => Number(row.pontuacao ?? 0),
            },
            {
                id: "status",
                header: "STATUS",
                accessor: "status",
                sortable: true,
                width: "140px",
            },
            ...(podeApagar
                ? [
                      {
                          id: "acoes",
                          header: "AÇÕES",
                          sortable: false,
                          width: "70px",
                          align: "center",
                          render: (_, row) => (
                              <AcoesCelula>
                                  <BotaoAcao
                                      $danger
                                      title="Remover"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          abrirDelete(row);
                                      }}
                                  >
                                      <FiTrash2 size={14} />
                                  </BotaoAcao>
                              </AcoesCelula>
                          ),
                      },
                  ]
                : []),
        ],
        [podeApagar, abrirDelete]
    );

    const handleRowClick = (row) => {
        if (!row?.id) return;
        window.open(`/modulo/ssma/prossmap/${row.id}`, "_blank", "noopener,noreferrer");
    };

    return (
        <>
            <PageContent>
                <GenericHeader responsive>
                    <Filters columns={3}>
                        <FilterContainer
                            type="text"
                            placeholder="NOME ENCARREGADO"
                            valor={nomeEncarregado}
                            changeFunc={setNomeEncarregado}
                        />
                        <FilterContainer
                            type="text"
                            placeholder="MATRÍCULA"
                            valor={matricula}
                            changeFunc={setMatricula}
                            maxLength={6}
                        />
                        <MultiselectCheck
                            options={opcoesEquipeMs}
                            selectedOptions={selectedEquipes}
                            setSelectedOptions={setSelectedEquipes}
                            placeholder="EQUIPE..."
                        />
                        <MultiselectCheck
                            options={opcoesCentroCustoMs}
                            selectedOptions={selectedCentrosCusto}
                            setSelectedOptions={setSelectedCentrosCusto}
                            placeholder="CENTRO CUSTO..."
                        />
                        <FilterContainer
                            type="month"
                            placeholder="MÊS"
                            valor={mesReferencia}
                            changeFunc={setMesReferencia}
                        />
                        <MultiselectCheck
                            options={opcoesStatusMs}
                            selectedOptions={selectedStatuses}
                            setSelectedOptions={setSelectedStatuses}
                            placeholder="STATUS..."
                        />
                    </Filters>

                    <Statistics>
                        <ContainerStatistic>
                            {STATUS_RESUMO.map((status) => (
                                <TextoStatistic key={status}>
                                    {status}: {resumoStatus[status]}
                                </TextoStatistic>
                            ))}
                            <TextoStatistic>TOTAL: {resumoStatus.total}</TextoStatistic>
                        </ContainerStatistic>
                    </Statistics>
                </GenericHeader>

                <TableFlexFill>
                    <SortableTable
                        columns={columns}
                        data={prossmapsFiltrados}
                        isLoading={loading}
                        loadingText="Carregando PROSSMAPs..."
                        emptyText="Nenhum PROSSMAP encontrado."
                        initialSort={{ key: "referencia", direction: "desc" }}
                        getRowId={(row) => row.id}
                        onRowClick={handleRowClick}
                        rowCursor="pointer"
                    />
                </TableFlexFill>
            </PageContent>

            {isModalOpen("prossmapDelete") && (
                <ModalDeleteProssmap
                    prossmap={prossmapParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={fetchProssmaps}
                />
            )}
        </>
    );
};

export default Content;
